import React, { useState, useCallback } from 'react';
import { Dropdown } from 'antd';
import {
  UserIcon,
  BellIcon,
  KeyIcon,
  SettingsIcon,
  LogoutIcon,
  ChevronDownIcon,
} from '@assets/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/helpers';
import { logout } from '@store/slices/auth/authSlice';
import { persistor } from '@store/store';
import { authApi } from '@store/api/auth/authApi';
import { projectsApi } from '@store/api/projects/projectsApi';
import { organizationsApi } from '@store/api/organizations/organizationsApi';
import { channelsApi } from '@store/api/channels/channelsApi';
import { bundlesApi } from '@store/api/bundles/bundlesApi';
import { invitesApi } from '@store/api/invites/invitesApi';
import { billingApi } from '@store/api/billing/billingApi';
import { tokensApi } from '@store/api/tokens/tokensApi';
import { useTheme } from '@context/ThemeContext';
import { notificationsApi, useGetNotificationsQuery } from '@store/api/notifications/notificationsApi';
import { clearStoragePreservingAnalyticsConsent } from '@/lib/firebase/consent';
import type { AppUserMenuDropdownProps } from './AppUserMenuDropdown.types';
import styles from './AppUserMenuDropdown.module.css';

export const AppUserMenuDropdown: React.FC<AppUserMenuDropdownProps> = ({
  userLabel,
  userName,
  isMobile = false,
  onLogout: onLogoutProp,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authToken = useAppSelector(state => state.auth.token);
  const { colors, theme, setTheme } = useTheme();
  const { data: notifications } = useGetNotificationsQuery(undefined, {
    skip: !authToken,
    refetchOnMountOrArgChange: true,
  });
  const hasUnreadNotifications = Boolean(notifications?.hasUnread);

  const handleLogout = useCallback(() => {
    if (onLogoutProp) {
      onLogoutProp();
      setOpen(false);
      return;
    }
    dispatch(logout());
    dispatch(authApi.util.resetApiState());
    dispatch(projectsApi.util.resetApiState());
    dispatch(organizationsApi.util.resetApiState());
    dispatch(channelsApi.util.resetApiState());
    dispatch(bundlesApi.util.resetApiState());
    dispatch(invitesApi.util.resetApiState());
    dispatch(notificationsApi.util.resetApiState());
    dispatch(billingApi.util.resetApiState());
    dispatch(tokensApi.util.resetApiState());
    void persistor.purge();
    if (typeof window !== 'undefined') {
      clearStoragePreservingAnalyticsConsent();
    }
    setOpen(false);
  }, [dispatch, onLogoutProp]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const dropdownContent = (
    <div className={styles.dropdown}>
      <div className={styles.dropdownUser} style={{ color: colors.text_500 }}>
        <UserIcon className={`${styles.menuIcon} ${styles.dropdownUserIcon}`} aria-hidden />
        <span className={styles.dropdownUserLabel}>{userLabel}</span>
      </div>
      <div className={styles.dropdownDivider} />
      <div className={styles.dropdownList}>
        <button
          type="button"
          className={styles.option}
          onClick={() => handleNavigate('/user/notifications')}
        >
          <BellIcon className={`${styles.menuIcon} ${styles.optionIcon}`} aria-hidden />
          <span className={styles.optionLabel}>
            {t('userMenu.notifications')}
            {hasUnreadNotifications ? <span className={styles.notificationDot} /> : null}
          </span>
        </button>
        <button
          type="button"
          className={styles.option}
          onClick={() => handleNavigate('/user/tokens')}
        >
          <KeyIcon className={`${styles.menuIcon} ${styles.optionIcon}`} aria-hidden />
          <span>{t('userMenu.patManagement')}</span>
        </button>
        <button
          type="button"
          className={styles.option}
          onClick={() => handleNavigate('/user/settings')}
        >
          <SettingsIcon className={`${styles.menuIcon} ${styles.optionIcon}`} aria-hidden />
          <span>{t('userMenu.settings')}</span>
        </button>
      </div>
      <div className={styles.dropdownDivider} />
      <div className={styles.themeSection}>
        <span className={styles.themeLabel}>{t('userMenu.theme')}</span>
        <div className={styles.themeSwitcher}>
          <button
            type="button"
            className={`${styles.themeButton} ${theme === 'dark' ? styles.themeButtonActive : ''}`}
            onClick={() => setTheme('dark')}
          >
            {t('userMenu.themeDark')}
          </button>
          <button
            type="button"
            className={`${styles.themeButton} ${theme === 'darker' ? styles.themeButtonActive : ''}`}
            onClick={() => setTheme('darker')}
          >
            {t('userMenu.themeDarker')}
          </button>
        </div>
      </div>
      <div className={styles.dropdownDivider} />
      <button type="button" className={styles.optionLogout} onClick={handleLogout}>
        <LogoutIcon className={`${styles.menuIcon} ${styles.optionIconLogout}`} aria-hidden />
        <span>{t('nav.logout')}</span>
      </button>
    </div>
  );

  const triggerContent = (
    <span className={styles.trigger}>
      {isMobile ? (
        <span className={styles.triggerIconWrap}>
          <UserIcon
            className={`${styles.menuIcon} ${styles.triggerIcon}`}
            style={{ color: colors.text_500 }}
            data-testid="menu-trigger-user-icon"
            aria-hidden
          />
          {hasUnreadNotifications ? <span className={styles.triggerDotMobile} /> : null}
        </span>
      ) : userName ? (
        <>
          <span className={styles.triggerText} style={{ color: colors.text_500 }}>
            {t('header.loggedInAs')}{' '}
          </span>
          <span className={styles.triggerUserWrap}>
            <span className={styles.triggerUserName}>
              {userName}
            </span>
            {hasUnreadNotifications ? <span className={styles.triggerDotDesktop} /> : null}
          </span>
        </>
      ) : (
        <span className={styles.triggerUserWrap}>
          <span className={styles.triggerText} style={{ color: colors.text_500 }}>
            {userLabel}
          </span>
          {hasUnreadNotifications ? <span className={styles.triggerDotDesktop} /> : null}
        </span>
      )}
      {!isMobile && (
        <ChevronDownIcon
          className={`${styles.menuIcon} ${styles.triggerArrow}`}
          style={{ color: colors.text_500 }}
          data-testid="menu-chevron-icon"
          aria-hidden
        />
      )}
    </span>
  );

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      menu={{ items: [] }}
      popupRender={() => dropdownContent}
      overlayClassName={styles.dropdownOverlay}
      placement="bottomRight"
    >
      <button
        type="button"
        className={styles.button}
        aria-label={userLabel}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {triggerContent}
      </button>
    </Dropdown>
  );
};
